import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSettings, digits } from "@/context/SettingsContext";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import HeroSlider from "@/components/home/HeroSlider";
import CinematicMoment from "@/components/home/CinematicMoment";
import InstagramStrip from "@/components/home/InstagramStrip";
import {
  Truck, HandHeart, Landmark, ArrowRight, Sparkles,
  Flame, Gift, Home as HomeIcon, Flower2, Crown, Grid3x3, Globe, Users,
  Package, Smartphone, Headphones, ChevronDown,
} from "lucide-react";
import { FAQS, fillFaq } from "@/data/faqs";

/*
  Adiyogi Handicrafts — conversion-first homepage (mobile-first).
  Section order:
    1. HeroSlider (dual CTAs + trust line)
    2. Best Sellers (immediately below hero, VIEW ALL always visible)
    3. Shop by Category
    4. Shop by Occasion (Daily Pooja · Diwali · Varalakshmi · Wedding · Housewarming · Temple)
    5. New Arrivals
    6. Why Choose Adiyogi Handicrafts (6 trust pillars)
    7. Customer Reviews (real reviews only — empty state until shared)
    8. Brand story (short) + Karnataka / EPCH trust + CinematicMoment
    9. Spiritual quote · Blog · Instagram · Newsletter
*/

// Fallback image for category cards when a backend image is missing.
const CATEGORY_TILES = [
  { name: "Pooja Items",          slug: "pooja-items",        icon: Flame,     fallback: "/products/thali-1.jpg" },
  { name: "Varalakshmi Pooja Sets", slug: "pooja-items",      icon: Flower2,   fallback: "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/2ki4p1ay_Image-89024%5B1%5D.jpg", query: "Varalakshmi" },
  { name: "German Silver Deepams", slug: "pooja-items",       icon: Sparkles,  fallback: "/products/thali-2.jpg", query: "Deepam" },
  { name: "Peacock Villaku",       slug: "pooja-items",       icon: Crown,     fallback: "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/73blf55p_IMG_20260819_224404%5B1%5D.jpg", query: "Peacock" },
  { name: "God Idols",             slug: "pooja-items",       icon: Landmark,  fallback: "/products/lakshmi-ganesh-1.jpg", query: "Idol" },
  { name: "Chambu / Kalash",       slug: "pooja-items",       icon: Gift,      fallback: "/products/chambu-1.jpg", query: "Chambu" },
  { name: "Housewarming Collection", slug: "housewarming",    icon: HomeIcon,  fallback: "/products/lakshmi-ganesh-1.jpg" },
  { name: "All Collections",       slug: "",                  icon: Grid3x3,   fallback: null, allCatalog: true },
];

const OCCASION_TILES = [
  { name: "Daily Pooja",       icon: Flame,    to: "/occasions/daily-pooja",        img: "/products/thali-1.jpg" },
  { name: "Diwali",            icon: Sparkles, to: "/deepavali-2026",               img: "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/73blf55p_IMG_20260819_224404%5B1%5D.jpg" },
  { name: "Varalakshmi Pooja", icon: Flower2,  to: "/shop?category=pooja-items&q=Varalakshmi", img: "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/2ki4p1ay_Image-89024%5B1%5D.jpg" },
  { name: "Wedding Gifts",     icon: Gift,     to: "/occasions/wedding-auspicious", img: "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/2ki4p1ay_Image-89024%5B1%5D.jpg" },
  { name: "Housewarming",      icon: HomeIcon, to: "/occasions/housewarming",       img: "/products/lakshmi-ganesh-1.jpg" },
  { name: "Temple",            icon: Landmark, to: "/shop?category=temple-decor",   img: "/products/thali-2.jpg" },
];

const WHY_PILLARS = [
  { icon: Sparkles,   title: "Traditional Indian Craftsmanship",   body: "Handcrafted by skilled artisans rooted in India's spiritual heritage." },
  { icon: Crown,      title: "Premium Handcrafted Collections",    body: "Curated German Silver, brass and bronze pieces with a premium finish." },
  { icon: Package,    title: "Carefully Packed for Safe Delivery", body: "Temple-grade protective packaging for every single order." },
  { icon: Truck,      title: "All India & Worldwide Shipping",     body: "Fast, reliable delivery across India and to homes worldwide." },
  { icon: Smartphone, title: "Easy Online Ordering",               body: "Order in minutes from your phone with simple prepaid UPI checkout." },
  { icon: Headphones, title: "Customer Support",                   body: "Quick, friendly help on WhatsApp for orders and product queries." },
];

export default function Home() {
  const [best, setBest] = useState([]);
  const [newest, setNewest] = useState([]);
  const [posts, setPosts] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [certOpen, setCertOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);
  const { settings } = useSettings();
  const waNumber = digits(settings.whatsapp_number);
  const reviewWaHref = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent("Hello Adiyogi Handicrafts, I'd like to share a review about my order.")}` : null;

  useEffect(() => {
    (async () => {
      const [b, n, bl, cats] = await Promise.all([
        api.get("/products", { params: { best_sellers: true, limit: 8 } }),
        api.get("/products", { params: { new_arrivals: true, limit: 8 } }),
        api.get("/blog", { params: { limit: 3 } }),
        api.get("/categories"),
      ]);
      setBest(b.data.items || []);
      setNewest(n.data.items || []);
      setPosts(bl.data || []);
      const map = {};
      (cats.data || []).forEach((c) => { map[c.slug] = c.image; });
      setCategoryImages(map);
    })();
  }, []);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Adiyogi Handicrafts",
    url: typeof window !== "undefined" ? window.location.origin : "",
    logo: `${typeof window !== "undefined" ? window.location.origin : ""}/adiyogi-logo.png`,
    sameAs: ["https://www.instagram.com/the_adiyogi_spiritual"],
  };

  const tileImage = (t) => t.fallback || categoryImages[t.slug] || "/adiyogi-hero.jpg";
  const tileHref = (t) => (t.allCatalog ? "/shop" : `/shop?category=${t.slug}${t.query ? `&q=${encodeURIComponent(t.query)}` : ""}`);

  return (
    <div data-testid="home-page">
      <SEO
        title="Premium Handcrafted Pooja & Spiritual Items — A Karnataka Government Initiative"
        description="Adiyogi Handicrafts — a Karnataka Government initiative. Premium German Silver pooja items, Varalakshmi sets, Peacock Villaku deepams, Chambu, Kalash and housewarming collections. Pan-India shipping · Prepaid UPI checkout."
        schema={orgSchema}
      />

      {/* 1 · HERO SLIDER (dual CTAs + trust line) */}
      <HeroSlider />

      {/* 2 · BEST SELLERS — immediately below the hero */}
      {best.length > 0 && (
        <section id="home-best-sellers" className="container-x py-10 md:py-14" data-testid="home-best-sellers">
          <div className="flex items-end justify-between mb-6 md:mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Loved by many</div>
              <h2 className="font-serif text-2xl md:text-4xl">Best Sellers</h2>
            </div>
            <Link to="/shop?best=1" className="btn-gold rounded-full px-4 md:px-5 py-2 text-[10px] md:text-xs uppercase tracking-widest flex items-center gap-1.5" data-testid="best-view-all">
              View All <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {best.map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </section>
      )}

      {/* 3 · SHOP BY CATEGORY */}
      <section className="bg-muted/40 border-y hairline" data-testid="featured-categories">
        <div className="container-x py-10 md:py-14">
          <div className="text-center mb-6 md:mb-8">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Curated Collections</div>
            <h2 className="font-serif text-2xl md:text-4xl">Shop by Category</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {CATEGORY_TILES.map((t) => (
              <Link
                key={t.name}
                to={tileHref(t)}
                className="group relative overflow-hidden rounded-xl border hairline aspect-[4/5] bg-white shadow-sm hover:shadow-md transition-shadow"
                data-testid={`cat-tile-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                <img
                  src={tileImage(t)}
                  alt={t.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 text-ivory">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#E8C878] mb-1.5">
                    <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Explore
                  </div>
                  <div className="font-serif text-lg md:text-2xl leading-tight">{t.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · SHOP BY OCCASION */}
      <section className="container-x py-10 md:py-14" data-testid="shop-by-occasion">
        <div className="text-center mb-6 md:mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">A gift for every moment</div>
          <h2 className="font-serif text-2xl md:text-4xl">Shop by Occasion</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {OCCASION_TILES.map((o) => (
            <Link
              key={o.name}
              to={o.to}
              className="group relative overflow-hidden rounded-xl border hairline aspect-[4/5] bg-white shadow-sm hover:shadow-md transition-shadow"
              data-testid={`occasion-${o.name.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}`}
            >
              <img src={o.img} alt={o.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-ivory">
                <div className="flex items-center gap-1.5 text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-[#E8C878] mb-1">
                  <o.icon className="w-3 h-3" strokeWidth={1.5} /> Shop
                </div>
                <div className="font-serif text-sm md:text-lg leading-tight">{o.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 5 · NEW ARRIVALS */}
      {newest.length > 0 && (
        <section className="bg-muted/40 border-y hairline" data-testid="home-new-arrivals">
          <div className="container-x py-10 md:py-14">
            <div className="flex items-end justify-between mb-6 md:mb-8">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] gold-text mb-2">Freshly added</div>
                <h2 className="font-serif text-2xl md:text-4xl">New Arrivals</h2>
              </div>
              <Link to="/shop?new=1" className="text-xs md:text-sm gold-text flex items-center gap-1.5 uppercase tracking-widest" data-testid="new-view-all">
                View All <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              {newest.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* 6 · WHY CHOOSE US */}
      <section className="container-x py-10 md:py-14" data-testid="why-choose">
        <div className="text-center mb-6 md:mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Reasons to shop with us</div>
          <h2 className="font-serif text-2xl md:text-4xl">Why Choose Adiyogi Handicrafts?</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {WHY_PILLARS.map((w) => (
            <div key={w.title} className="border hairline rounded-xl p-4 md:p-6 bg-white hover:shadow-sm transition-shadow">
              <div className="w-9 h-9 rounded-full bg-[#B8860B]/10 flex items-center justify-center gold-text mb-3">
                <w.icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="font-serif text-base md:text-lg leading-tight mb-1.5">{w.title}</div>
              <div className="text-xs text-muted-foreground leading-relaxed">{w.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 7 · CUSTOMER REVIEWS (real reviews only — hidden until genuine reviews are shared) */}
      <section className="bg-muted/40 border-y hairline" data-testid="customer-reviews">
        <div className="container-x py-10 md:py-14">
          <div className="text-center mb-6 md:mb-8">
            <div className="text-xs uppercase tracking-[0.3em] gold-text mb-2">Verified Buyers</div>
            <h2 className="font-serif text-2xl md:text-4xl">Loved by Our Customers</h2>
          </div>
          <div className="max-w-2xl mx-auto border hairline rounded-xl p-5 md:p-8 bg-white text-center" data-testid="reviews-empty-state">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#B8860B]/10 flex items-center justify-center gold-text mb-4">
              <Users className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="font-serif text-lg leading-tight mb-2">Your review could be the first one shown here.</div>
            <div className="text-xs text-muted-foreground mb-5">
              Verified customers can share a star rating, photo and note about their order. We never post fabricated reviews.
            </div>
            {reviewWaHref && <a
              href={reviewWaHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2"
              data-testid="submit-review-wa"
            >
              Share Your Review on WhatsApp
            </a>}
          </div>
        </div>
      </section>

      {/* 8 · CINEMATIC MOMENT (brand moment, after shopping sections) */}
      <CinematicMoment />

      {/* 9 · BRAND STORY (short) */}
      <section className="container-x py-10 md:py-14" data-testid="brand-intro">
        <div className="grid md:grid-cols-[220px_1fr] gap-8 md:gap-12 items-center">
          <div className="justify-self-center md:justify-self-start">
            <img
              src="/adiyogi-logo.png"
              alt="Adiyogi Handicrafts"
              className="w-40 md:w-52 h-auto object-contain drop-shadow-sm"
              loading="lazy"
            />
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">
              <Sparkles className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} />
              Adiyogi Handicrafts
            </div>
            <h2 className="font-serif text-2xl md:text-4xl leading-tight mb-3">
              Where Tradition Meets <span className="gold-text italic">Timeless Craftsmanship</span>
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mb-5 max-w-2xl">
              Traditional Indian artistry and spiritual elegance — beautifully crafted pooja and
              devotional pieces for homes, temples, festivals and auspicious occasions.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/about"
                className="btn-outline-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-widest"
                data-testid="brand-story-cta"
              >
                Discover Our Story
              </Link>
              <Link
                to="/shop"
                className="btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-widest"
                data-testid="brand-shop-cta"
              >
                Shop the Collection
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 10 · KARNATAKA + EPCH TRUST SECTION */}
      <section className="relative overflow-hidden bg-espresso text-ivory" data-testid="karnataka-section">
        <div
          className="absolute inset-0 opacity-30 bg-center bg-cover"
          style={{ backgroundImage: "url(/adiyogi-hero.jpg)" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/55" />
        <div className="relative container-x py-12 md:py-16 grid md:grid-cols-[1fr_380px] items-center gap-10 md:gap-14">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.45em] text-[#E8C878] mb-4">
              <Landmark className="w-3.5 h-3.5" strokeWidth={1.5} />
              Government-Recognised · Karnataka Associated
            </div>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-5">
              A <span className="gold-text italic">Karnataka Government</span> Initiative
            </h2>
            <p className="text-sm md:text-base text-ivory/85 max-w-2xl leading-relaxed mb-3">
              Adiyogi Handicrafts is presented as a Karnataka Government initiative — supporting the
              promotion and presentation of traditional Indian craftsmanship and spiritual heritage.
            </p>
            <p className="text-sm md:text-base text-ivory/80 max-w-2xl leading-relaxed">
              Officially awarded a <span className="text-[#E8C878] font-medium">Certificate of Merit</span> by the
              <span className="text-[#E8C878]"> Export Promotion Council for Handicrafts (EPCH)</span>, sponsored by the
              <span className="text-[#E8C878]"> Ministry of Textiles, Government of India</span>, for outstanding
              regional export performance in the Southern region (1995–96).
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="btn-gold rounded-full px-7 py-2.5 text-xs uppercase tracking-widest"
                data-testid="karnataka-about-cta"
              >
                Read About Us
              </Link>
              <button
                onClick={() => setCertOpen(true)}
                className="rounded-full px-7 py-2.5 text-xs uppercase tracking-widest border border-[#E8C878]/60 text-ivory hover:bg-white/10 transition"
                data-testid="view-certificate-cta"
              >
                View Certificate
              </button>
            </div>
          </div>

          {/* Official EPCH Certificate of Merit — click to enlarge */}
          <div className="justify-self-center md:justify-self-end w-full max-w-[360px]">
            <button
              type="button"
              onClick={() => setCertOpen(true)}
              className="group block w-full rounded-lg overflow-hidden bg-white/5 backdrop-blur-sm border border-[#E8C878]/40 shadow-2xl transition-transform hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(184,134,11,0.35)]"
              data-testid="epch-certificate-card"
              aria-label="Open Certificate of Merit"
            >
              <div className="p-3 md:p-4 bg-white/95">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/w4xsq7ra_file_0000000061cc8208a66c5e46c48bf3d2%5B1%5D.png"
                  alt="Certificate of Merit awarded to Adiyogi Handicrafts by Export Promotion Council for Handicrafts (EPCH), Ministry of Textiles, Government of India — Southern region, 1995-96"
                  loading="lazy"
                  className="w-full h-auto object-contain aspect-[3/4] transition-transform duration-700 group-hover:scale-[1.02]"
                  data-testid="epch-certificate-image"
                />
              </div>
              <div className="p-4 md:p-5 text-left">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#E8C878] mb-1.5">Official Recognition</div>
                <div className="font-serif text-lg text-ivory leading-tight">Certificate of Merit</div>
                <div className="text-[11px] text-ivory/70 mt-1 leading-relaxed">
                  Export Promotion Council for Handicrafts (EPCH)<br />
                  Ministry of Textiles · Government of India<br />
                  Southern Region · 1995–96
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest gold-text opacity-90 group-hover:opacity-100">
                  Tap to view <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                </div>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Certificate lightbox */}
      {certOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setCertOpen(false)}
          data-testid="certificate-lightbox"
        >
          <button
            onClick={() => setCertOpen(false)}
            aria-label="Close certificate"
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-ivory text-xl leading-none"
            data-testid="certificate-close"
          >
            ×
          </button>
          <div
            className="relative max-w-3xl max-h-[92vh] bg-white rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/w4xsq7ra_file_0000000061cc8208a66c5e46c48bf3d2%5B1%5D.png"
              alt="Certificate of Merit awarded to Adiyogi Handicrafts by EPCH, Ministry of Textiles, Government of India — Southern region, 1995-96"
              className="w-full h-auto max-h-[92vh] object-contain"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5 text-ivory">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#E8C878]">Official Recognition</div>
              <div className="font-serif text-lg leading-tight">
                Certificate of Merit · EPCH · Ministry of Textiles, Government of India · 1995-96
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 11 · SPIRITUAL QUOTE */}
      <section className="relative bg-ivory border-y hairline overflow-hidden" data-testid="spiritual-quote">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #B8860B 0px, transparent 40%), radial-gradient(circle at 80% 70%, #B8860B 0px, transparent 45%)",
          }}
        />
        <div className="relative container-x py-10 md:py-14 text-center">
          <div className="text-[10px] uppercase tracking-[0.5em] gold-text mb-4">A Reflection</div>
          <blockquote className="font-serif text-2xl md:text-4xl leading-snug text-espresso max-w-3xl mx-auto italic">
            “Let every corner of your home carry the <span className="gold-text not-italic">light of tradition</span>.”
          </blockquote>
          <div className="mt-6 flex justify-center">
            <span className="inline-block w-16 h-px bg-[#B8860B]/50" />
          </div>
        </div>
      </section>

      {/* 12 · BLOG */}
      {posts.length > 0 && (
        <section className="container-x py-10 md:py-14" data-testid="home-blog">
          <div className="flex items-end justify-between mb-6 md:mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Guides &amp; Rituals</div>
              <h2 className="font-serif text-2xl md:text-4xl">From the Journal</h2>
            </div>
            <Link to="/blog" className="text-sm gold-text hidden md:inline">Read all</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="border hairline rounded-xl overflow-hidden bg-white group" data-testid={`blog-${p.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-widest gold-text mb-2">{p.category}</div>
                  <div className="font-serif text-xl leading-tight">{p.title}</div>
                  <div className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 13 · INSTAGRAM STRIP */}
      <InstagramStrip />

      {/* 13.5 · FAQ STRIP (top questions + view all) */}
      <section className="container-x py-10 md:py-14" data-testid="home-faq">
        <div className="text-center mb-6 md:mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">Help Centre</div>
          <h2 className="font-serif text-2xl md:text-4xl">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto border hairline rounded-xl bg-white overflow-hidden divide-y divide-[rgba(184,134,11,0.15)]">
          {FAQS.slice(0, 6).map((f, i) => {
            const isOpen = faqOpen === i;
            return (
              <div key={i} data-testid={`home-faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setFaqOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 text-left px-4 md:px-6 py-3.5 md:py-4 hover:bg-muted/40 transition-colors"
                  data-testid={`home-faq-q-${i}`}
                >
                  <span className="font-serif text-base md:text-lg leading-snug">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 shrink-0 gold-text transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.5} />
                </button>
                <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <p className="px-4 md:px-6 pb-4 text-sm text-muted-foreground leading-relaxed" data-testid={`home-faq-a-${i}`}>
                      {fillFaq(f.a, settings)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-6">
          <Link to="/faq" className="btn-gold rounded-full px-7 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="view-all-faqs">
            View All FAQs <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* 14 · NEWSLETTER (kept) */}
      <section className="bg-muted/50 border-y hairline">
        <div className="container-x py-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] gold-text mb-2">Join the Adiyogi Circle</div>
            <h3 className="font-serif text-3xl md:text-4xl">Rituals, launches &amp; muhurat guides</h3>
            <p className="text-sm text-muted-foreground mt-3">Occasional emails — no spam, just craft &amp; culture.</p>
          </div>
          <form className="flex gap-2" onSubmit={(e) => e.preventDefault()} data-testid="newsletter-form">
            <input type="email" placeholder="you@email.com" className="flex-1 border hairline rounded-full px-5 py-3 bg-white outline-none text-sm" data-testid="newsletter-email" />
            <button className="btn-gold rounded-full px-6 text-xs uppercase tracking-widest" data-testid="newsletter-submit">Subscribe</button>
          </form>
        </div>
      </section>
    </div>
  );
}
